import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import { LikeFilled } from '@ant-design/icons';
import { CommentResponse } from 'models/comment';
import { useCallback, useMemo, useState } from 'react';
import { selectCurrentLanguage } from 'redux/userSlice';
import moment from 'moment';
import useUserDetail from 'hooks/useUserDetail';
import oddsApi from 'api/oddsApi';
import ConditionButton from 'components/Commons/ConditionButton';
import { updateResult } from 'redux/gameSlice';
import { checkEmptyString } from 'utils/commonUtils';

interface CommentItemProps {
  comment: CommentResponse;
  gameId: string;
  isParent: boolean;
  hideReply: boolean;
  setHideReply?: (value: boolean) => void;
  getComments: () => void;
}

const CommentItem: React.FunctionComponent<CommentItemProps> = ({
  isParent,
  hideReply,
  setHideReply,
  comment,
  gameId,
  getComments,
}) => {
  const t = useAppTranslation();
  const dispatch = useAppDispatch();
  const language = useAppSelector(selectCurrentLanguage);
  const { currentUser, isUserLoggedIn } = useUserDetail();
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [content, setContent] = useState<string>('');

  const handleReply = () => {
    setShowReplyBox((prev) => !prev);
  };

  const handleHideReply = () => {
    if (setHideReply) {
      setHideReply(!hideReply);
    }
  };

  const isLiked = useMemo(() => {
    if (currentUser) {
      return comment.listUser.userIds.includes(currentUser.id);
    }
    return false;
  }, [comment, currentUser?.id]);

  const onChangeComment = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (event.target.value.length < 500) {
      setContent(event.target.value);
    }
  };

  const handleEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendComment();
    }
  };

  const sendComment = useCallback(async () => {
    if (comment && isUserLoggedIn) {
      const { ok } = await oddsApi.commentGame({
        content: content,
        gameId: gameId,
        parentId: comment.id,
      });
      if (ok) {
        setContent('');
        getComments();
        dispatch(updateResult());
        setShowReplyBox(false);
        setHideReply && setHideReply(false);
      }
    }
  }, [gameId, content, comment.id]);

  const likeComment = useCallback(async () => {
    const { ok } = await oddsApi.likeComment(comment.id);
    if (ok) {
      getComments();
    }
  }, [comment.id]);

  return (
    <div className="comment-wrapper">
      <div className="author">
        <p>{checkEmptyString(comment.username) ? t('anonymous') : comment.username}</p>
        <div className="meta">
          {moment(comment.createdDate)
            .locale(language === 'zh' ? 'zh-cn' : language)
            .fromNow()}
        </div>
      </div>
      <div className="text">{comment.content}</div>

      <div className="comment-button">
        <ConditionButton
          className={`like-button ${isLiked ? 'active' : ''}`}
          content={
            <div>
              <LikeFilled /> {comment.likeCounter}
            </div>
          }
          testerCheck={true}
          onClick={likeComment}
          skipKyc
        />

        {isParent && (
          <div className="reply-button">
            {comment.numberOfReply > 0 && (
              <p onClick={handleHideReply}>
                {hideReply
                  ? t('comment.unhide', { amount: comment.numberOfReply })
                  : t('comment.hide', { amount: comment.numberOfReply })}
              </p>
            )}
            <ConditionButton
              content={<p>{t('comment.reply')}</p>}
              testerCheck={true}
              onClick={handleReply}
              skipKyc
            />
          </div>
        )}
      </div>
      {isParent && showReplyBox && isUserLoggedIn && (
        <div className="reply-box">
          <textarea
            placeholder="Reply..."
            onKeyUp={handleEnter}
            onChange={onChangeComment}
            value={content}
          ></textarea>
          <div className="send-button">
            <button type="button" onClick={sendComment}>
              {t('comment.reply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default CommentItem;
